import { Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * `medium` (default) — page-level tabs: 16px / bold (Company settings, Platform
 * admin, etc.). `small` — the narrower, lighter, left-aligned variant used in the
 * side drawers/asides (Data Analysis view panel, document Editor drawer).
 */
export type UnderlineTabsSize = 'small' | 'medium';

const SIZE_STYLES: Record<
  UnderlineTabsSize,
  { fontSize: number; fontWeight: number; gap: number; padding: string; minHeight: number }
> = {
  small: { fontSize: 12, fontWeight: 600, gap: 24, padding: '0.5rem 0', minHeight: 40 },
  medium: { fontSize: 16, fontWeight: 700, gap: 32, padding: '0.5rem 0', minHeight: 48 },
};

export const UnderlineTabs = styled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: UnderlineTabsSize }>(({ theme, size = 'medium' }) => {
  const s = SIZE_STYLES[size];
  return {
    minHeight: s.minHeight,
    border: 0,
    borderBottom: '1px solid #e0e0e0',

    // Keep tabs left-aligned regardless of variant.
    '& .MuiTabs-list': {
      border: 0,
      padding: 0,
      gap: s.gap,
      justifyContent: 'flex-start',
    },

    '& .MuiTab-root': {
      minHeight: s.minHeight,
      minWidth: 0,
      padding: s.padding,
      fontSize: s.fontSize,
      fontWeight: s.fontWeight,
      textTransform: 'none',
      backgroundColor: 'transparent',
      '&.Mui-selected': {
        backgroundColor: 'transparent',
      },
    },

    // Scrollable variant: a disabled scroll button (e.g. "back" while at the
    // start) otherwise reserves its full width and indents the first tab. Collapse
    // it so tabs stay flush-left.
    '& .MuiTabs-scrollButtons.Mui-disabled': {
      width: 0,
      overflow: 'hidden',
    },

    '& .MuiTabs-indicator': {
      height: 3,
      top: 'calc(100% - 3px)',
      zIndex: 2,
      backgroundColor: theme.palette.primary.main,
    },
  };
});

export const UnderlineTab = styled(Tab)({});
